import { CommonTexts } from '../services/CommonTexts';
import type { ApiJsonObject, ApiResponse, ApiResult } from './types/api.types';

/** Query parameters of a GET — the PHP side reads them as strings. */
export type ApiParams = Record<string, string>;

/** The HTTP layer of ApiService: one envelope, one result shape for every method. Modules use it, callers do not. */
export class ApiClient {
    private static readonly BasePath = '/api';

    public async delete<TBody>(path: string): Promise<ApiResult<TBody>> {
        return this.request<TBody>(path, 'DELETE');
    }

    /** GET, `params` as query string. */
    public async get<TBody>(path: string, params?: ApiParams): Promise<ApiResult<TBody>> {
        const Query = params === undefined ? '' : `?${new URLSearchParams(params).toString()}`;

        return this.request<TBody>(`${path}${Query}`, 'GET');
    }

    /** PATCH — only the fields sent change. */
    public async patch<TBody>(path: string, body: ApiJsonObject): Promise<ApiResult<TBody>> {
        return this.request<TBody>(path, 'PATCH', body);
    }

    /** POST, `body` as JSON (`HttpRequest::listBodyData()` / `listPostParams()` read it). */
    public async post<TBody>(path: string, body: ApiJsonObject = {}): Promise<ApiResult<TBody>> {
        return this.request<TBody>(path, 'POST', body);
    }

    /** PUT — every writable field anew; missing ones are emptied. */
    public async put<TBody>(path: string, body: ApiJsonObject): Promise<ApiResult<TBody>> {
        return this.request<TBody>(path, 'PUT', body);
    }

    /**
     * Decides by the HTTP status and the ack: a 200 with `status: failure` is a refusal with its reason in `errors`
     * (a person a user still uses). `status` may be null on responses that set no ack — that counts as success.
     */
    private static buildResult<TBody>(envelope: ApiResponse<TBody>): ApiResult<TBody> {
        const Http = envelope.header.http;
        const Status = envelope.response.status;
        const IsOk = Http >= 200 && Http < 300 && Status !== 'failure' && Status !== 'invalid';

        return {
            isOk: IsOk,
            http: Http,
            body: IsOk ? envelope.body : undefined,
            errors: envelope.response.errors ?? [],
        };
    }

    /** http 0 marks a client-side failure, so callers handle one failure shape instead of exceptions plus failed envelopes. */
    private static buildFailure(message: string): ApiResult<never> {
        return { isOk: false, http: 0, body: undefined, errors: [{ type: 'error', message, inx: 'NETWORK' }] };
    }

    /** `body` is sent as JSON; without one no `Content-Type` is sent. */
    private async request<TBody>(path: string, method: 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT', body?: ApiJsonObject): Promise<ApiResult<TBody>> {
        try {
            const Result = await fetch(`${ApiClient.BasePath}${path}`, {
                method,
                headers: body === undefined ? { Accept: 'application/json' } : { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                body: body === undefined ? undefined : JSON.stringify(body),
                credentials: 'same-origin',
            });

            // Error responses use the same envelope, so the body is read regardless of the HTTP status.
            return ApiClient.buildResult((await Result.json()) as ApiResponse<TBody>);
        } catch {
            return ApiClient.buildFailure(CommonTexts.ApiUnreachable);
        }
    }
}
